import React, { useState, useEffect } from 'react';
import { fetchTaxBrackets, createTaxBracket, updateTaxBracket, deleteTaxBracket } from '../api';

const BURS_DEFAULT_BRACKETS = [
  { lower_limit: 0, upper_limit: 48000, rate: 0, description: '0 – 48 000 (0%)' },
  { lower_limit: 48001, upper_limit: 84000, rate: 5, description: '48 001 – 84 000 (5%)' },
  { lower_limit: 84001, upper_limit: 120000, rate: 12.5, description: '84 001 – 120 000 (12.5%)' },
  { lower_limit: 120001, upper_limit: 156000, rate: 18.75, description: '120 001 – 156 000 (18.75%)' },
  { lower_limit: 156001, upper_limit: 999999999, rate: 25, description: '156 001+ (25%)' },
];

const TaxTableManager = ({ authAxios, addToast }) => {
  const [brackets, setBrackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ min_income: '', max_income: '', marginal_rate: '', base_tax: '0', tax_year: new Date().getFullYear(), period_type: 'ANNUAL' });
  const [showAdd, setShowAdd] = useState(false);

  const loadBrackets = async () => {
    setLoading(true);
    try {
      const data = await fetchTaxBrackets(authAxios);
      setBrackets(data.results || data);
    } catch {
      addToast('Failed to load tax brackets', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadBrackets(); }, []);

  const handleSave = async () => {
    try {
      if (editId) {
        await updateTaxBracket(authAxios, editId, form);
        addToast('Bracket updated', 'success');
      } else {
        await createTaxBracket(authAxios, form);
        addToast('Bracket created', 'success');
      }
      setEditId(null);
      setShowAdd(false);
      setForm({ min_income: '', max_income: '', marginal_rate: '', base_tax: '0', tax_year: new Date().getFullYear(), period_type: 'ANNUAL' });
      loadBrackets();
    } catch (err) {
      addToast('Failed to save bracket', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tax bracket?')) return;
    try {
      await deleteTaxBracket(authAxios, id);
      addToast('Bracket deleted', 'success');
      loadBrackets();
    } catch {
      addToast('Failed to delete bracket', 'error');
    }
  };

  const handleEdit = (bracket) => {
    setEditId(bracket.id);
    setForm({
      min_income: bracket.min_income,
      max_income: bracket.max_income,
      marginal_rate: bracket.marginal_rate,
      base_tax: bracket.base_tax || '0',
      tax_year: bracket.tax_year,
      period_type: bracket.period_type || 'ANNUAL',
    });
    setShowAdd(true);
  };

  // Group by tax year
  const years = [...new Set(brackets.map(b => b.tax_year))].sort((a, b) => b - a);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">BURS Tax Tables</h2>
          <p className="text-sm text-gray-500">Manage PAYE tax brackets per year</p>
        </div>
        <button
          onClick={() => { setShowAdd(true); setEditId(null); setForm({ min_income: '', max_income: '', marginal_rate: '', base_tax: '0', tax_year: new Date().getFullYear(), period_type: 'ANNUAL' }); }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Add Bracket
        </button>
      </div>

      {/* BURS Reference */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-sm">
        <strong>BURS Reference (Annual):</strong>
        <div className="flex flex-wrap gap-3 mt-1">
          {BURS_DEFAULT_BRACKETS.map((b, i) => (
            <span key={i} className="bg-white border rounded px-2 py-1 text-xs">{b.description}</span>
          ))}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <div className="bg-gray-50 border rounded p-4 mb-4">
          <h3 className="font-semibold mb-3">{editId ? 'Edit' : 'Add'} Tax Bracket</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs text-gray-600">Tax Year</label>
              <input type="number" className="border rounded p-2 w-full text-sm" value={form.tax_year} onChange={e => setForm({ ...form, tax_year: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Lower Limit (BWP)</label>
              <input type="number" step="0.01" className="border rounded p-2 w-full text-sm" value={form.min_income} onChange={e => setForm({ ...form, min_income: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Upper Limit (BWP)</label>
              <input type="number" step="0.01" className="border rounded p-2 w-full text-sm" value={form.max_income} onChange={e => setForm({ ...form, max_income: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Base Tax (BWP)</label>
              <input type="number" step="0.01" className="border rounded p-2 w-full text-sm" value={form.base_tax} onChange={e => setForm({ ...form, base_tax: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Marginal Rate (%)</label>
              <input type="number" step="0.01" className="border rounded p-2 w-full text-sm" value={form.marginal_rate} onChange={e => setForm({ ...form, marginal_rate: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-600">Period</label>
              <select className="border rounded p-2 w-full text-sm" value={form.period_type} onChange={e => setForm({ ...form, period_type: e.target.value })}>
                <option value="ANNUAL">Annual</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={handleSave} className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">
              {editId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => { setShowAdd(false); setEditId(null); }} className="px-4 py-2 bg-gray-200 rounded text-sm hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <p className="text-center py-8">Loading...</p> : years.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No tax brackets configured. Use the management command or add them manually.</p>
      ) : (
        years.map(year => {
          const yearBrackets = brackets.filter(b => b.tax_year === year).sort((a, b) => parseFloat(a.min_income) - parseFloat(b.min_income));
          return (
            <div key={year} className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Tax Year {year}</h3>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border-b p-2 text-right">Lower Limit</th>
                    <th className="border-b p-2 text-right">Upper Limit</th>
                    <th className="border-b p-2 text-right">Base Tax</th>
                    <th className="border-b p-2 text-right">Rate (%)</th>
                    <th className="border-b p-2 text-left">Period</th>
                    <th className="border-b p-2 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {yearBrackets.map(b => (
                    <tr key={b.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 text-right">P {parseFloat(b.min_income).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                      <td className="p-2 text-right">{b.max_income ? `P ${parseFloat(b.max_income).toLocaleString('en', { minimumFractionDigits: 2 })}` : '—'}</td>
                      <td className="p-2 text-right">P {parseFloat(b.base_tax || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                      <td className="p-2 text-right">{b.marginal_rate}%</td>
                      <td className="p-2">{b.period_type || 'ANNUAL'}</td>
                      <td className="p-2 space-x-1 text-right">
                        <button onClick={() => handleEdit(b)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Edit</button>
                        <button onClick={() => handleDelete(b.id)} className="px-2 py-1 bg-red-500 text-white rounded text-xs">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
};

export default TaxTableManager;
